import type { APIError } from '@vizit/api';
import { Badge, BadgeLevel, BadgeShape, Image, Skeleton, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { Image01Icon } from '@vizit/icons';
import { EmptyCollection } from '@vizit/illustrations';
import { ContextMenuMode } from '@vizit/molecules';
import { colors, cva, cx } from '@vizit/theme';
import React from 'react';
import { type AriaButtonOptions, type PressEvent, mergeProps, useButton, useFocusWithin, useHover } from 'react-aria';
import { CollectionMenuDropdown } from '../collection-menu-dropdown';

export interface CollectionCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'id' | 'onPress'> {
  /**
   * Unique identifier for the collection
   */
  id: string;
  /**
   * The title of the collection
   */
  title?: string;
  /**
   * Number of items in the collection
   */
  itemCount: number;
  /**
   * Optional preview image URL (typically the thumbnail)
   */
  previewImageUrl?: string | null;
  /**
   * Optional fallback preview image URL used when the primary image fails to load
   */
  previewImageFallbackUrl?: string | null;
  /**
   * Badge text (e.g., category name)
   */
  badgeText?: string;
  /**
   * Custom background color for the badge
   */
  badgeBackgroundColor?: string | null;
  /**
   * Custom border color for the badge
   */
  badgeBorderColor?: string | null;
  /**
   * Whether the card is in a loading state
   */
  isLoading?: boolean;
  /**
   * Callback when the card is clicked/pressed
   */
  onPress?: (id: string, event: PressEvent) => void;
  /**
   * Custom className for the card
   */
  className?: string;
  /**
   * Portfolio ID for collection operations
   */
  portfolioId?: string;
  /**
   * Organization ID for collection operations
   */
  organizationId?: string;
  /**
   * Callback when a collection is renamed
   */
  onRename?: (collectionId: string, newName: string) => void;
  /**
   * Callback when a collection is deleted
   */
  onDelete?: (collectionId: string) => void;
  /**
   * Callback when an authentication error occurs
   */
  onAuthenticationError?: (error: APIError) => void;
}

const cardClasses = cva({
  base: [
    'group relative cursor-pointer outline-none transition-all ease-in-out',
    'border border-transparent hover:border-gray-utility-700 focus:hover:border-transparent',
    'bg-white',
    'focus:ring-1 focus:border-transparent focus:ring-gray-utility-700 focus:ring-offset-0',
    'w-full',
    'p-2',
    'flex flex-col gap-4',
  ],
  variants: {
    isLoading: {
      true: 'cursor-wait opacity-50',
      false: '',
    },
  },
});

const imageContainerClasses = cva({
  base: ['relative w-full aspect-square', 'bg-white', 'flex items-center justify-center'],
});

const placeholderClasses = cva({
  base: ['flex items-center justify-center w-full h-full border border-secondary'],
});

const contentClasses = cva({
  base: ['flex flex-col gap-4 w-full'],
});

const titleClasses = cva({
  base: ['truncate group-hover:text-tertiary-hover'],
});

const bottomClasses = cva({
  base: ['flex items-center justify-between w-full'],
});

const badgeClasses = cva({
  base: ['truncate max-w-[140px]'],
});

const imageCountClasses = cva({
  base: ['flex items-center gap-2'],
});

export const CollectionCard = (props: CollectionCardProps) => {
  const {
    id,
    title,
    itemCount,
    previewImageUrl,
    previewImageFallbackUrl,
    badgeText,
    badgeBackgroundColor,
    badgeBorderColor,
    isLoading = false,
    onPress,
    className,
    portfolioId,
    organizationId,
    onRename,
    onDelete,
    onAuthenticationError,
    ...other
  } = props;

  const ref = React.useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  const isEmptyCollection = itemCount === 0 && !isLoading;

  const hasCustomColors = badgeBackgroundColor && badgeBorderColor;

  const handlePress = React.useCallback(
    (event: PressEvent) => {
      if (!isLoading) {
        onPress?.(id, event);
      }
    },
    [id, isLoading, onPress],
  );

  const { hoverProps } = useHover({ onHoverChange: setIsHovered, isDisabled: isLoading });
  const { focusWithinProps } = useFocusWithin({ onFocusWithinChange: setIsFocused, isDisabled: isLoading });
  const { buttonProps } = useButton(
    {
      ...(other as AriaButtonOptions<'div'>),
      onPress: handlePress,
      elementType: 'div',
      isDisabled: isLoading,
    },
    ref,
  );

  const showMenu = !isLoading && onRename && onDelete && onAuthenticationError;

  return (
    <div
      ref={ref}
      className={cx(cardClasses({ isLoading }), className)}
      {...mergeProps(buttonProps, hoverProps, focusWithinProps)}
      data-collection-id={id}
      data-focus={isFocused}
      data-hovered={isHovered}
    >
      {/* Collection Menu Dropdown */}
      {showMenu && (
        <div className="absolute top-4 right-4 z-10" onClick={(e) => e.stopPropagation()}>
          <CollectionMenuDropdown
            collectionId={id}
            collectionName={title ?? ''}
            portfolioId={portfolioId}
            organizationId={organizationId}
            mode={ContextMenuMode.Dark}
            isVisible={isHovered || isFocused}
            onSuccessfulRename={(newName: string) => onRename(id, newName)}
            onSuccessfulDelete={onDelete}
          />
        </div>
      )}

      {/* Image/Preview Section */}
      <div className={imageContainerClasses()}>
        {!isEmptyCollection ? (
          <Image
            src={previewImageUrl ?? null}
            fallbackSrc={previewImageFallbackUrl ?? null}
            isAnalyzing={isLoading}
            name={title ?? ''}
            shape="square"
            className="w-full h-full"
          />
        ) : (
          <div className={placeholderClasses()}>
            <EmptyCollection />
          </div>
        )}
      </div>

      <div className={contentClasses()}>
        {isLoading ? (
          <Skeleton variant="square" className="w-full h-10" />
        ) : (
          <Text
            size={TextSize.Sm}
            weight={TextWeight.Medium}
            color={TextColor.Tertiary}
            className={titleClasses()}
            title={title}
          >
            {title}
          </Text>
        )}

        <div className={bottomClasses()}>
          {isLoading ? (
            <Skeleton variant="square" className="w-30 h-12" />
          ) : (
            <Badge
              shape={BadgeShape.Box}
              className={cx(badgeClasses())}
              {...(hasCustomColors
                ? { style: { backgroundColor: badgeBackgroundColor, borderColor: badgeBorderColor } }
                : { level: BadgeLevel.Brand })}
            >
              <span className="truncate text-xs" {...(hasCustomColors ? { style: { color: badgeBorderColor } } : {})}>
                {badgeText}
              </span>
            </Badge>
          )}

          <div className={imageCountClasses()}>
            {isLoading ? (
              <>
                <Skeleton variant="square" className="w-8 h-8" />

                <Skeleton variant="square" className="w-10 h-6" />
              </>
            ) : (
              <>
                <Image01Icon size={16} color={colors['gray-utility']['400']} />

                <Text size={TextSize.Xs} weight={TextWeight.Medium} color={TextColor.Quaternary}>
                  {itemCount}
                </Text>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

CollectionCard.displayName = 'CollectionCard';
